import React, { useEffect, useState } from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminHeaders } from '../utils/adminHeaders';
import {Typography, Grid, Sheet, CardContent, Divider, Card, CircularProgress, Alert, Stack, Box, IconButton, Chip, Select, Option} from '@mui/joy';
import {
    Person,
    ArrowBack,
    Apps,
    DeviceHub,
    Edit,
    Email,
    Badge,
} from '@mui/icons-material';
import HubCard from "../components/cards/HubCard";
import ClientAppCard from "../components/cards/ClientAppCard";
import ClientProfileCard from "../components/cards/ClientProfileCard";
import PermissionWrapper from "../components/PermissionWrapper";
import ConfirmActionButton from "../components/ConfirmActionButton";


const API_BASE = process.env.REACT_APP_ADMIN_API_BASE || 'http://localhost:9300';

const ClientDetailsPage = () => {
    const { session } = useAuth();
    const { clientId } = useParams();
    const navigate = useNavigate();

    const [profile, setProfile] = useState(null);
    const [apps, setApps] = useState([]);
    const [hubs, setHubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadApps = async () => {
        const appsRes = await fetch(`${API_BASE}/client/${clientId}/apps`, { headers: adminHeaders(session) });
        if (appsRes.ok) {
            const appsData = await appsRes.json();
            // Ensure apps is always an array
            setApps(Array.isArray(appsData) ? appsData : []);
        } else {
            console.error('Failed to fetch apps:', appsRes.status);
            setApps([]); // Fallback to empty array
        }
    };

    // Signs installations out (admin:write); throws a readable error for the button.
    const terminate = async (path, method) => {
        const res = await fetch(`${API_BASE}/client/${clientId}/sessions/${path}`, {
            method,
            headers: adminHeaders(session),
        });
        if (!res.ok) {
            const body = await res.json().catch(() => null);
            throw new Error((body && (body.error || body)) || `Request failed (${res.status})`);
        }
        await loadApps();
    };

    useEffect(() => {
        const fetchClientData = async () => {
            try {
                setLoading(true);
                setError(null);

                // Fetch profile
                const profileRes = await fetch(`${API_BASE}/client/${clientId}/profile`, { headers: adminHeaders(session) });
                if (profileRes.ok) {
                    const profileData = await profileRes.json();
                    setProfile(profileData);
                } else {
                    console.error('Failed to fetch profile:', profileRes.status);
                }

                // Fetch apps
                await loadApps();

                // Fetch hubs
                const hubsRes = await fetch(`${API_BASE}/client/${clientId}/hubs`, { headers: adminHeaders(session) });
                if (hubsRes.ok) {
                    const hubsData = await hubsRes.json();
                    // Ensure hubs is always an array
                    setHubs(Array.isArray(hubsData) ? hubsData : []);
                } else {
                    console.error('Failed to fetch hubs:', hubsRes.status);
                    setHubs([]); // Fallback to empty array
                }

            } catch (error) {
                console.error('Error fetching client data:', error);
                setError('Failed to load client information');
            } finally {
                setLoading(false);
            }
        };

        fetchClientData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId, session?.accessToken]);

    const handleHubClick = (hubId) => {
        navigate(`/hub/${hubId}`);
    };

    if (loading) {
        return (
            <Sheet sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <Stack spacing={2} alignItems="center">
                    <CircularProgress size="lg" />
                    <Typography level="body-lg">Loading client details...</Typography>
                </Stack>
            </Sheet>
        );
    }

    if (error) {
        return (
            <Sheet sx={{ p: 4 }}>
                <Alert color="danger" variant="soft" sx={{ mb: 3 }}>
                    <Typography>{error}</Typography>
                </Alert>
                <Typography level="body-sm" textColor="neutral.500">
                    Please try refreshing the page or contact support if the problem persists.
                </Typography>
            </Sheet>
        );
    }

    return (
        <Sheet sx={{ p: 4, maxWidth: '1400px', mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <IconButton 
                        variant="outlined" 
                        onClick={() => navigate(-1)}
                        sx={{ borderRadius: '50%' }}
                    >
                        <ArrowBack />
                    </IconButton>
                    <Person sx={{ fontSize: 32, color: 'success.500' }} />
                    <Box>
                        <Typography level="h2" sx={{ mb: 0.5 }}>
                            Client Profile
                        </Typography>
                        <Typography level="body-lg" textColor="neutral.600">
                            {profile ? 'View and manage client account details' : 'Loading client information...'}
                        </Typography>
                    </Box>
                </Stack>
            </Box>

            {/* Profile Information */}
            {profile && (
                <Box sx={{ mb: 4 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                backgroundColor: 'success.softBg',
                                color: 'success.500',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Person sx={{ fontSize: 24 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography level="h4" sx={{ mb: 0.5 }}>
                                Profile Information
                            </Typography>
                            <Typography level="body-sm" textColor="neutral.600">
                                Client account details and settings
                            </Typography>
                        </Box>
                    </Stack>

                    <Card variant="outlined">
                    <CardContent>
                            <Grid container spacing={3}>
                                <Grid xs={12} md={4}>
                                    <Stack spacing={2}>
                                        <Box>
                                            <Typography level="body-sm" textColor="neutral.600" sx={{ mb: 1 }}>
                                                Full Name
                                            </Typography>
                                            <Typography level="title-md">
                                                {profile.name}
                                            </Typography>
                                        </Box>

                                        <Box>
                                            <Typography level="body-sm" textColor="neutral.600" sx={{ mb: 1 }}>
                                                Email Address
                                            </Typography>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Email sx={{ fontSize: 16, color: 'neutral.500' }} />
                                                <Typography level="body-md">
                                                    {profile.email}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </Grid>

                                <Grid xs={12} md={4}>
                                    <Stack spacing={2}>
                                        <Box>
                                            <Typography level="body-sm" textColor="neutral.600" sx={{ mb: 1 }}>
                                                Log Level
                                            </Typography>
                                            <Select
                                value={profile.log_level}
                                                onChange={(event, newValue) => {
                                                    setProfile({ ...profile, log_level: newValue });

                                                    fetch(`${API_BASE}/client/${clientId}`, {
                                        method: 'PATCH',
                                        headers: adminHeaders(session, { 'Content-Type': 'application/json' }),
                                                        body: JSON.stringify({ log_level: newValue }),
                                    }).then(res => {
                                        if (!res.ok) {
                                            console.error('Failed to update client log level');
                                        }
                                    });
                                }}
                                                variant="outlined"
                                                size="sm"
                            >
                                {['trace', 'debug', 'info', 'warning', 'error'].map(lvl => (
                                                    <Option key={lvl} value={lvl}>
                                                        {lvl.charAt(0).toUpperCase() + lvl.slice(1)}
                                                    </Option>
                                ))}
                                            </Select>
                                        </Box>

                                        <Box>
                                            <Typography level="body-sm" textColor="neutral.600" sx={{ mb: 1 }}>
                                                Created At
                                            </Typography>
                                            <Typography level="body-md">
                                                {new Date(profile.created_at).toLocaleString()}
                                            </Typography>
                                        </Box>

                                        {profile.deleted_at && (
                                            <Box>
                                                <Typography level="body-sm" textColor="neutral.600" sx={{ mb: 1 }}>
                                                    Deleted At
                                                </Typography>
                                                <Typography level="body-md" color="danger">
                                                    {new Date(profile.deleted_at).toLocaleString()}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Stack>
                                </Grid>

                                <Grid xs={12} md={4}>
                                    <Stack spacing={2}>
                                        <Box>
                                            <Typography level="body-sm" textColor="neutral.600" sx={{ mb: 1 }}>
                                                Client ID
                                            </Typography>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Badge sx={{ fontSize: 16, color: 'neutral.500' }} />
                                                <Typography level="body-md" fontFamily="monospace">
                                                    {profile.id}
                                                </Typography>
                                            </Stack>
                                        </Box>

                                        <Box>
                                            <Typography level="body-sm" textColor="neutral.600" sx={{ mb: 1 }}>
                                                Account Status
                                            </Typography>
                                            <Chip 
                                                color={profile.deleted_at ? "danger" : "success"} 
                                                variant="soft"
                                                size="sm"
                                            >
                                                {profile.deleted_at ? "Deleted" : "Active"}
                                            </Chip>
                                        </Box>
                                    </Stack>
                                </Grid>
                            </Grid>
                    </CardContent>
                </Card>
                </Box>
            )}

            <Divider sx={{ my: 4 }} />

            {/* Applications Section */}
            <Box sx={{ mb: 4 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            backgroundColor: 'primary.softBg',
                            color: 'primary.500',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Apps sx={{ fontSize: 24 }} />
                    </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography level="h4" sx={{ mb: 0.5 }}>
                                Applications
                            </Typography>
                            <Typography level="body-sm" textColor="neutral.600">
                                Installations (app on a device), their devices and sessions
                            </Typography>
                        </Box>
                        <Chip color="primary" variant="soft" size="sm">
                            {apps.length} app{apps.length === 1 ? '' : 's'}
                        </Chip>
                        {apps.some((app) => app.signed_in) && (
                            <PermissionWrapper requiredPermission="admin:write">
                                <ConfirmActionButton
                                    label="Terminate all sessions"
                                    title="Sign out all of this client's devices?"
                                    message={`${apps.filter((app) => app.signed_in).length} signed-in device(s) will have to sign in again and stop receiving push notifications.`}
                                    confirmLabel="Sign out all"
                                    onConfirm={() => terminate('terminate-all', 'POST')}
                                />
                            </PermissionWrapper>
                        )}
                    </Stack>

                    {apps.length > 0 ? (
                        <Grid container spacing={3}>
                {apps.map((app) => (
                    <Grid key={app.id} xs={12} md={6} lg={4}>
                        <ClientAppCard app={app} onTerminate={() => terminate(app.id, 'DELETE')} />
                    </Grid>
                ))}
                        </Grid>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Apps sx={{ fontSize: 48, color: 'neutral.400', mb: 2 }} />
                            <Typography level="h4" sx={{ mb: 1 }}>
                                No Applications
                            </Typography>
                            <Typography level="body-sm" textColor="neutral.500">
                                This client doesn't have any applications registered yet.
                            </Typography>
                        </Box>
                )}
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Connected Hubs Section */}
            <Box>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            backgroundColor: 'warning.softBg',
                            color: 'warning.500',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <DeviceHub sx={{ fontSize: 24 }} />
                    </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography level="h4" sx={{ mb: 0.5 }}>
                                Connected Hubs
                            </Typography>
                            <Typography level="body-sm" textColor="neutral.600">
                                Devices and hubs owned by this client
                            </Typography>
                        </Box>
                        <Chip color="warning" variant="soft" size="sm">
                            {hubs.length} hub{hubs.length === 1 ? '' : 's'}
                        </Chip>
                    </Stack>

                    {hubs.length > 0 ? (
                        <Grid container spacing={3}>
                {hubs.map((hub) => (
                    <Grid
                        key={hub.id}
                        xs={12}
                        md={6}
                        lg={4}
                        onClick={() => handleHubClick(hub.id)}
                        sx={{ cursor: 'pointer' }}
                    >
                        <HubCard hub={hub} />
                    </Grid>
                ))}
                        </Grid>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <DeviceHub sx={{ fontSize: 48, color: 'neutral.400', mb: 2 }} />
                            <Typography level="h4" sx={{ mb: 1 }}>
                                No Connected Hubs
                            </Typography>
                            <Typography level="body-sm" textColor="neutral.500">
                                This client doesn't have any connected hubs or devices yet.
                            </Typography>
                        </Box>
                )}
            </Box>
        </Sheet>
    );
};

export default ClientDetailsPage;
